import { createTheme } from '@mui/material/styles';

// ═══════════════════════════════════════════════════════════════
// BINKO DESIGN SYSTEM v1.0 — MASTER THEME
// ═══════════════════════════════════════════════════════════════
// This is the single source of truth for all colors, typography,
// and spacing across the Binko app.
//
// TO CHANGE A COLOR SITE-WIDE: edit it here only.
// Never hardcode color hex values in component files.
// Always import from this file or use the MUI theme tokens.
//
// Usage in components:
//   import { C, T, SP } from '../theme';   // raw tokens
//   OR rely on MUI sx={{ color: 'primary.main' }} etc.
// ═══════════════════════════════════════════════════════════════

// ── RAW COLOR TOKENS ────────────────────────────────────────────
// Import and use these directly when MUI theme tokens don't reach
// (e.g. inline style objects, canvas, SVG)

export const C = {
  // Primary palette
  yellow:       '#FBBC15',   // Action — CTAs, active nav, selected chips, like button
  yellowText:   '#5A3A00',   // Text ON yellow backgrounds only
  teal:         '#0D9DA5',   // Connection — match badge, chat icon, check-in dot
  orange:       '#FE7210',   // Urgency — notifications, unread, live game, new likes

  // Supporting palette
  tealFill:     '#43ABAE',   // Teal bg only, at ≤20% opacity — never for text/icons
  bgApp:        '#0D0D0D',   // Root app background — always
  bgCard:       '#1A1A1A',   // Card surfaces — venue cards, user cards, chat bubbles sent
  bgElevated:   '#2A2A2A',   // Bottom nav, modals, input fields
  textPrimary:  '#EFEFEF',   // Names, headings, body copy
  textMuted:    '#888888',   // Timestamps, metadata, inactive labels
  textDisabled: '#555555',   // Placeholder, inactive icons, disabled labels
  border:       '#2A2A2A',   // Card borders, dividers, input outlines
  success:      '#22C55E',   // Transient success flash only — check-in confirm, sent. Gone in 1.5s.

  // Semantic aliases (use these in components for clarity)
  action:       '#FBBC15',   // = yellow
  connection:   '#0D9DA5',   // = teal
  urgency:      '#FE7210',   // = orange
  surface:      '#1A1A1A',   // = bgCard
  surfaceHigh:  '#2A2A2A',   // = bgElevated

  // ⚠️  BANNED COLORS — never use in any component:
  // purple:  any purple — not a Binko color
  // green:   #00e5a0, #00ff00 etc — only #22C55E success flash allowed
  // pink:    #ff4d8f etc — old palette, fully replaced
  // white:   #FFFFFF — use #EFEFEF instead (too harsh on dark bg)
  // black:   #000000 — use #0D0D0D instead
};

// ── SPACING TOKENS ───────────────────────────────────────────────
export const SP = {
  xs:   4,    // Icon-to-label, inline gaps
  sm:   8,    // Between chips, badge padding
  md:   12,   // Card internal gap, between list items
  lg:   16,   // Section internal padding
  xl:   20,   // Between sections
  xxl:  32,   // Screen-level vertical breathing room
  screenH: 18, // Horizontal screen padding — both sides, always
  cardPad: 14, // Card internal padding
};

// ── COMPONENT SIZE TOKENS ────────────────────────────────────────
export const SZ = {
  btnPrimary:   48,   // Primary button height
  btnSecondary: 40,   // Secondary button height
  btnChip:      32,   // Chip height
  navHeight:    56,   // Bottom nav height (+ safe area inset)
  topBar:       56,   // Status bar + top bar total
  avatarLg:     48,   // Large avatar (browse cards)
  avatarSm:     36,   // Small avatar (chat list)
  iconNav:      22,   // Bottom nav icons
  iconCard:     18,   // In-card functional icons
  iconMeta:     14,   // Metadata / decorative icons
};

// ── MUI THEME ────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main:         C.yellow,
      contrastText: C.yellowText,
    },
    secondary: {
      main:         C.teal,
      contrastText: '#FFFFFF',
    },
    error: {
      main:         C.orange,
      contrastText: '#FFFFFF',
    },
    warning: {
      main:         C.orange,
      contrastText: '#FFFFFF',
    },
    success: {
      main:         C.success,
      contrastText: '#FFFFFF',
    },
    background: {
      default: C.bgApp,
      paper:   C.bgCard,
    },
    text: {
      primary:   C.textPrimary,
      secondary: C.textMuted,
      disabled:  C.textDisabled,
    },
    divider: C.border,
  },

  typography: {
    fontFamily: '"Plus Jakarta Sans", system-ui, -apple-system, sans-serif',
    // Scale
    h1: { fontSize: '22px', fontWeight: 600, color: C.textPrimary, letterSpacing: '-0.3px', lineHeight: 1.2 },
    h2: { fontSize: '17px', fontWeight: 600, color: C.textPrimary, letterSpacing: '-0.3px', lineHeight: 1.2 },
    h3: { fontSize: '15px', fontWeight: 500, color: C.textPrimary, lineHeight: 1.4 },
    body1: { fontSize: '13px', fontWeight: 400, color: C.textPrimary, lineHeight: 1.4 },
    body2: { fontSize: '12px', fontWeight: 400, color: C.textMuted,  lineHeight: 1.4 },
    caption: { fontSize: '11px', fontWeight: 500, color: C.textMuted, letterSpacing: '0.04em' },
    overline: { fontSize: '10px', fontWeight: 400, color: C.textMuted },
  },

  shape: {
    borderRadius: 14, // default card radius
  },

  components: {
    // ── GLOBAL BASELINE ──────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: `
        
        * { box-sizing: border-box; }
        body {
          background-color: ${C.bgApp};
          color: ${C.textPrimary};
          font-family: "Plus Jakarta Sans", system-ui, -apple-system, sans-serif;
          -webkit-font-smoothing: antialiased;
        }
      `,
    },

    // ── BUTTONS ──────────────────────────────────────────────────
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          fontSize: '13px',
          fontWeight: 600,
          borderRadius: '12px',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          // Yellow CTA — one per screen max
          background: C.yellow,
          color: C.yellowText,
          height: SZ.btnPrimary,
          '&:hover': { background: '#e6aa10' },
          '&.Mui-disabled': { background: C.bgElevated, color: C.textDisabled },
        },
        outlinedSecondary: {
          // Secondary / back / cancel
          border: `0.5px solid ${C.border}`,
          color: C.textPrimary,
          height: SZ.btnSecondary,
          '&:hover': { border: `0.5px solid ${C.textMuted}`, background: 'transparent' },
        },
        outlinedError: {
          // Destructive — check out, unmatch, delete
          border: `0.5px solid ${C.orange}`,
          color: C.orange,
          height: SZ.btnSecondary,
          '&:hover': { background: 'rgba(254,114,16,0.08)' },
        },
      },
    },

    // ── CARDS ────────────────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: {
          background: C.bgCard,
          border: `0.5px solid ${C.border}`,
          borderRadius: '14px',
          padding: `${SP.cardPad}px`,
          boxShadow: 'none',
          transition: 'border-color 0.15s',
          '&:hover': { borderColor: C.yellow },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          background: C.bgCard,
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },

    // ── INPUT FIELDS ─────────────────────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: C.bgCard,
          borderRadius: '10px',
          fontSize: '14px',
          color: C.textPrimary,
          '&:not(.MuiInputBase-multiline)': { height: '44px' },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: C.border,
            borderWidth: '0.5px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: C.textMuted,
            borderWidth: '0.5px',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: C.yellow,
            borderWidth: '0.5px',
          },
        },
        input: {
          '&::placeholder': { color: C.textDisabled, opacity: 1 },
        },
      },
    },

    // ── CHIPS ────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          fontSize: '11px',
          fontWeight: 500,
          height: `${SZ.btnChip}px`,
          borderRadius: '99px',
          background: C.bgCard,
          border: `0.5px solid ${C.border}`,
          color: C.textMuted,
        },
        colorPrimary: {
          // Selected/active chip
          background: '#1F1900',
          border: `0.5px solid ${C.yellow}`,
          color: C.yellow,
        },
        colorSecondary: {
          // Teal selected chip
          background: '#0A1F22',
          border: `0.5px solid ${C.teal}`,
          color: C.teal,
        },
      },
    },

    // ── AVATAR ───────────────────────────────────────────────────
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          fontWeight: 600,
          background: C.teal,       // Connection color for avatars
          color: '#FFFFFF',
        },
      },
    },

    // ── CIRCULAR PROGRESS ────────────────────────────────────────
    MuiCircularProgress: {
      styleOverrides: {
        root: { color: C.yellow },
      },
    },

    // ── SNACKBAR / ALERT ─────────────────────────────────────────
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          fontSize: '13px',
          borderRadius: '12px',
        },
      },
    },

    // ── BOTTOM SHEET / MODAL ─────────────────────────────────────
    MuiModal: {
      styleOverrides: {
        backdrop: { background: 'rgba(0,0,0,0.7)' },
      },
    },

    // ── DIVIDER ──────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: C.border, borderWidth: '0.5px' },
      },
    },
  },
});

export default theme;
